import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  SvgIcon, ICON_AI,
  HOME_ITEM, FILES_ITEM, PRICING_ITEM, AI_TOOLS_ITEMS,
  type NavItem,
} from './navConfig';

type GroupKey = 'ai';

const linkBase = 'flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors';
const linkActive = 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400';
const linkIdle = 'text-gray-600 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800/60';

/** Top-level leaf link (Home, File Manager). */
function TopNavLink({ to, icon, label }: { to: string; icon: ReactNode; label: string }) {
  return (
    <NavLink to={to} className={({ isActive }) => cn(linkBase, isActive ? linkActive : linkIdle)}>
      {icon}
      {label}
    </NavLink>
  );
}

/** Top-level group trigger (AI Tools / Admin) + its click-open dropdown panel. */
function TopNavGroup({
  label, icon, items, active, isOpen, onToggle, onClose,
}: {
  label: string; icon: ReactNode; items: NavItem[];
  active: boolean; isOpen: boolean; onToggle: () => void; onClose: () => void;
}) {
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={onToggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={cn(linkBase, (active || isOpen) ? linkActive : linkIdle)}
      >
        {icon}
        {label}
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute left-0 top-full z-50 mt-1.5 min-w-[200px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
            role="menu"
          >
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                role="menuitem"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'text-gray-700 dark:text-zinc-300 hover:bg-gray-50 dark:hover:bg-zinc-800'
                  )
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Primary horizontal nav row for the Header dashboard layout — replaces Sidebar's nav content. */
export function HeaderNavBar() {
  const [openGroup, setOpenGroup] = useState<GroupKey | null>(null);
  const wrapperRef = useRef<HTMLElement>(null);
  const location = useLocation();

  // Active-section detection — mirrors Sidebar's exact route-group matching.
  // IMPORTANT: every useMatch must be called unconditionally — never inside ||/&& short-circuits.
  const _matchAiTools = !!useMatch({ path: '/ai-tools/*', end: false });
  const _matchJournals = !!useMatch({ path: '/journals/*', end: false });
  const onAiRoute = _matchAiTools || _matchJournals;

  useEffect(() => {
    if (openGroup === null) return;
    const onDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpenGroup(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenGroup(null); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [openGroup]);

  // Defensive backstop — the primary close mechanism is each dropdown item's own onClick.
  useEffect(() => {
    setOpenGroup(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key: GroupKey) => setOpenGroup((cur) => (cur === key ? null : key));
  const closeGroup = () => setOpenGroup(null);

  return (
    <nav ref={wrapperRef} aria-label="Primary" className="flex min-w-0 items-center gap-1">
      {/* Brand */}
      <div className="mr-1 flex shrink-0 items-center gap-2 border-r border-gray-200 pr-3 dark:border-zinc-800">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-electric-500 to-neon-cyan shadow-sm">
          <svg className="h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          </svg>
        </span>
        <span className="hidden whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-zinc-100 xl:inline">
          Sci<span className="bg-gradient-to-r from-electric-500 to-neon-cyan bg-clip-text font-bold text-transparent dark:from-electric-400">Campus</span>
        </span>
      </div>

      <TopNavLink to={HOME_ITEM.to} icon={HOME_ITEM.icon} label={HOME_ITEM.label} />

      <TopNavGroup
        label="AI Tools"
        icon={<SvgIcon path={ICON_AI} />}
        items={AI_TOOLS_ITEMS}
        active={onAiRoute}
        isOpen={openGroup === 'ai'}
        onToggle={() => toggleGroup('ai')}
        onClose={closeGroup}
      />

      <TopNavLink to={FILES_ITEM.to} icon={FILES_ITEM.icon} label={FILES_ITEM.label} />

      <TopNavLink to={PRICING_ITEM.to} icon={PRICING_ITEM.icon} label={PRICING_ITEM.label} />
    </nav>
  );
}
